import { rowToPerson } from './roleHelpers.js'
// Bare shared-library import, not a deep _safeMap.js subpath — see app.js's
// identical fix note (tickets/catalog-fixall.md P0). Deferred-call wrapper,
// not a module-scope destructure — see app.js's circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
const safeMap = (...args) => _wsShared.functions.safeMap(...args)

// Source of truth for `currentUserEmail` on initial page load.
//
// History:
//   - Pre-2026-04-27: read from a kv `app_settings` table (dev `useRealAuth=false` fixture).
//   - 2026-04-27 migration: pointed at `workspaceProject.companySettings`. That route
//     only declares `{ get, update }` ops (EntityDispatcher.js) — `method: 'select'`
//     maps to `list`, which the dispatcher rejects: "does not support op 'list'".
//     Even when callable, `company_settings` carries valuation columns, not user
//     identity — `current_user_email` was never present there. Net effect: pages
//     whose grids filter by `root.currentUserEmail` (calendar week/team views)
//     rendered empty whenever the auth subscriber hadn't fired yet (e.g. on a
//     stale governance JWT — auth-js was supposed to silently refresh but
//     sometimes doesn't, and the page-level fetch was the safety net).
//
// Now: route through the SDK's `auth.session` entity. That entity always returns
// the live SDK session (email, _id, username, supabaseUserId) — independent of
// any legacy token remnants, so even when a refresh stalls
// we still hydrate root with the user's identity. Includes the `_settings` key
// in the transform return so the fetch plugin's smart-spread heuristic
// (`key in data` check in updateElementState) treats every top-level field as
// a root-state write rather than nesting them under `root._settings`.
export const appSettingsFetch = {
  method: 'select',
  from: 'auth.session',
  as: '~/_settings',
  transform: (session) => {
    const u = (session && session.user) || {}
    const email = u.email || ''
    return {
      // Trigger the fetch plugin's smart-spread (sees `_settings` in the
      // returned object) so the rest of these keys land on `root.*` directly.
      _settings: { currentUserEmail: email, companyName: 'Symbols' },
      currentUserEmail: email,
      currentUserId: String(u._id || u.id || ''),
      currentUserUsername: u.username || '',
      currentUserSupabaseId: u.supabaseUserId || '',
      companyName: 'Symbols'
    }
  }
}

export const peopleFetch = {
  // `method: 'select'` routes through the SDK adapter's `select` handler,
  // which internally calls sdk.execute(from, 'list', …). This is the correct
  // path — `method: 'list'` is not in ADAPTER_METHODS (['select', 'rpc',
  // 'insert', 'update', 'delete', 'subscribe']), so declaring it there caused
  // adapter.list to be undefined and the fetch to silently return {data:null},
  // leaving root.people unpopulated and the invitee autocomplete empty (#2255).
  method: 'select',
  // Migrated from workspaceProject.people (the legacy people
  // view) to users.members (main-server route /core/users/people —
  // canonical Mongo User identity joined with workspace-extension
  // user_profiles). Row shape is identical.
  from: 'users.members',
  // Scope by PAYLOAD, not by the ambient claim: an explicit workspaceId
  // makes the server resolve THAT workspace's org (membership-verified,
  // same contract listOrgMembers.js uses), so a request issued mid-switch
  // returns the roster of the workspace the tab is viewing — not whatever
  // claim happens to be active when it lands. The workspaceId also lands
  // in the fetch-plugin cache key, so cached pages are tenant-scoped too.
  // No workspace resolved yet (boot) → {} keeps the legacy claim-scoped
  // read (EntityDispatcher maps an empty arg to [undefined]).
  params: (el, s) => {
    const root = s?.root || s
    const workspaceId =
      root?.workspace?.id || root?.activeWorkspaceId || root?.jwtClaims?.active_workspace_id
    return workspaceId ? { workspaceId } : {}
  },
  order: 'name',
  as: '~/people',
  // WORKSPACE-TICKETS-STATUS-MAP-NONARRAY: Array.isArray guard via safeMap —
  // `rows || []` lets a truthy non-array envelope pass through to .map().
  transform: (rows) => ({ people: safeMap(rows, rowToPerson) })
}

export const appPageBoilerplateFetch = [appSettingsFetch, peopleFetch]
